import { DataCoveragePanel } from "./accountManagementDataCoverage";
import type {
  DataCoverageClinic,
  DataCoverageResponse,
  PipelineCoverageClinic,
} from "./accountManagementTypes";

const AS_OF = "2026-08-19";

const pipelineClinics: PipelineCoverageClinic[] = [
  {
    id: "northwest-neurology",
    name: "Northwest Neurology",
    state: "WA",
    navigator: "Alex Rivera",
    eligibleCarespaceCount: 599,
    futureAppointmentCount: 81,
    futureAppointmentRate: 81 / 599,
    aiAttempt1wCount: 205,
    aiAttempt1wRate: 205 / 599,
    aiAttempt2wCount: 277,
    aiAttempt2wRate: 277 / 599,
    aiAttempt3wCount: 383,
    aiAttempt3wRate: 383 / 599,
    aiAttempt4wCount: 458,
    aiAttempt4wRate: 458 / 599,
    untouched4wCount: 112,
    untouched4wRate: 112 / 599,
    isPortfolioTotal: false,
  },
  {
    id: "texas-institute",
    name: "Texas Institute for Neurological Disorders",
    state: "TX",
    navigator: "Morgan Lee",
    eligibleCarespaceCount: 722,
    futureAppointmentCount: 118,
    futureAppointmentRate: 118 / 722,
    aiAttempt1wCount: 145,
    aiAttempt1wRate: 145 / 722,
    aiAttempt2wCount: 145,
    aiAttempt2wRate: 145 / 722,
    aiAttempt3wCount: 145,
    aiAttempt3wRate: 145 / 722,
    aiAttempt4wCount: 145,
    aiAttempt4wRate: 145 / 722,
    untouched4wCount: 476,
    untouched4wRate: 476 / 722,
    isPortfolioTotal: false,
  },
  {
    id: "lone-star-neurology",
    name: "Lone Star Neurology",
    state: "TX",
    navigator: "Alex Rivera",
    eligibleCarespaceCount: 584,
    futureAppointmentCount: 103,
    futureAppointmentRate: 103 / 584,
    aiAttempt1wCount: 246,
    aiAttempt1wRate: 246 / 584,
    aiAttempt2wCount: 327,
    aiAttempt2wRate: 327 / 584,
    aiAttempt3wCount: 458,
    aiAttempt3wRate: 458 / 584,
    aiAttempt4wCount: 536,
    aiAttempt4wRate: 536 / 584,
    untouched4wCount: 29,
    untouched4wRate: 29 / 584,
    isPortfolioTotal: false,
  },
];

function sum(key: keyof PipelineCoverageClinic): number {
  return pipelineClinics.reduce((total, clinic) => total + Number(clinic[key] || 0), 0);
}

const pipelineTotal: PipelineCoverageClinic = {
  id: "portfolio-total",
  name: "Portfolio total",
  state: "",
  navigator: "",
  eligibleCarespaceCount: sum("eligibleCarespaceCount"),
  futureAppointmentCount: sum("futureAppointmentCount"),
  futureAppointmentRate: sum("futureAppointmentCount") / sum("eligibleCarespaceCount"),
  aiAttempt1wCount: sum("aiAttempt1wCount"),
  aiAttempt1wRate: sum("aiAttempt1wCount") / sum("eligibleCarespaceCount"),
  aiAttempt2wCount: sum("aiAttempt2wCount"),
  aiAttempt2wRate: sum("aiAttempt2wCount") / sum("eligibleCarespaceCount"),
  aiAttempt3wCount: sum("aiAttempt3wCount"),
  aiAttempt3wRate: sum("aiAttempt3wCount") / sum("eligibleCarespaceCount"),
  aiAttempt4wCount: sum("aiAttempt4wCount"),
  aiAttempt4wRate: sum("aiAttempt4wCount") / sum("eligibleCarespaceCount"),
  untouched4wCount: sum("untouched4wCount"),
  untouched4wRate: sum("untouched4wCount") / sum("eligibleCarespaceCount"),
  isPortfolioTotal: true,
};

function dataCoverageClinic(clinic: PipelineCoverageClinic): DataCoverageClinic {
  const eligible = clinic.eligibleCarespaceCount;
  return {
    id: clinic.id,
    name: clinic.name,
    state: clinic.state,
    emrName: clinic.state === "TX" ? "eClinicalWorks" : "Epic",
    emrAccess: "yes",
    reportAccess: "yes",
    navigator: clinic.navigator,
    eligiblePatientCount: eligible,
    mbiCoveredPatientCount: Math.round(eligible * 0.91),
    missingMbiPatientCount: Math.round(eligible * 0.09),
    mbiCoverageRate: 0.91,
    dobCoveredPatientCount: Math.round(eligible * 0.96),
    dobCoverageRate: 0.96,
    phoneCoveredPatientCount: Math.round(eligible * 0.94),
    phoneCoverageRate: 0.94,
    icd10CoveredPatientCount: Math.round(eligible * 0.82),
    icd10CoverageRate: 0.82,
    providerCoveredPatientCount: Math.round(eligible * 0.88),
    providerCoverageRate: 0.88,
    npiCoveredPatientCount: Math.round(eligible * 0.79),
    npiCoverageRate: 0.79,
    pverifyCoveredPatientCount: Math.round(eligible * 0.87),
    pverifyCoverageRate: 0.87,
    allDataCoveredPatientCount: Math.round(eligible * 0.68),
    allDataCoverageRate: 0.68,
    isPortfolioTotal: false,
  };
}

const previewData: DataCoverageResponse = {
  source: "mock",
  generatedAt: "2026-08-19T16:00:00.000Z",
  asOf: AS_OF,
  timezone: "America/New_York",
  clinics: pipelineClinics.map(dataCoverageClinic),
  pipeline: {
    clinics: [...pipelineClinics, pipelineTotal],
  },
};

export function isAccountManagementPipelinePreview(location: {
  hostname: string;
  pathname: string;
}): boolean {
  if (location.pathname !== "/previews/account-management-pipeline-health") return false;
  return (
    location.hostname === "localhost" ||
    location.hostname === "127.0.0.1" ||
    /^deploy-preview-\d+--operators-ui\.netlify\.app$/.test(location.hostname)
  );
}

export function AccountManagementPipelinePreview() {
  return (
    <main className="h-full overflow-y-auto overscroll-y-auto bg-muted/20 px-4 py-8 text-foreground sm:px-8">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>PR preview · representative data.</strong> No production patient data is loaded on
          this page.
        </div>
        <DataCoveragePanel data={previewData} />
      </div>
    </main>
  );
}
